/**
 * Keys of the backend feature registry (`team/features.py`), mirrored by
 * hand the same way `Perm` mirrors `rbac/permissions.py`.
 *
 * `/my-account` ships `features`: one FeatureState per key the viewer's
 * permissions admit. A nav item, a landing-route candidate, a dashboard
 * button or a route guard asks nothing more than "what state is my key"
 * — the plan, the Settings → Features toggle, any beta flag and the
 * permissions are composed once, server side.
 *
 * Adding a feature: register it on the backend, add the key here, declare
 * it on the nav item(s) of both shells. Nothing else changes.
 */
export const FeatureKey = {
  dashboard: 'dashboard',
  approvalRequests: 'approval_requests',
  reports: 'reports',
  aiAssistant: 'ai_assistant',
  workflows: 'workflows',
  actions: 'actions',
  tasks: 'tasks',
  mailbox: 'mailbox',
  team: 'team',
  costCenters: 'cost_centers',
  bills: 'bills',
  purchaseOrders: 'purchase_orders',
  rfqs: 'rfqs',
  vendors: 'vendors',
  invoices: 'invoices',
  quotes: 'quotes',
  customers: 'customers',
  contracts: 'contracts',
  products: 'products',
  accounts: 'accounts',
  categories: 'categories',
  taxes: 'taxes',
  companyDetails: 'company_details',
  teamSettings: 'team_settings',
  settings: 'settings',
} as const

export type FeatureKey = (typeof FeatureKey)[keyof typeof FeatureKey]

/**
 * How a feature resolved for the viewer (`team.features.FeatureState`).
 * A key that is ABSENT from `/my-account.features` was denied by the
 * permissions or a beta flag (or is an enforced feature switched off).
 *
 * - `usable`: offer it (nav, landing, buttons) and admit it (routes).
 * - `locked`: the plan lacks it. Offer it locked with a paywall link;
 *   routes still admit (the page is where the paywall lives).
 * - `hidden`: switched off in Settings → Features. Cosmetic: nav drops
 *   it, a typed URL still opens it, as that screen promises.
 */
export const FeatureState = {
  usable: 'usable',
  locked: 'locked',
  hidden: 'hidden',
} as const

export type FeatureState = (typeof FeatureState)[keyof typeof FeatureState]
